import React, { useState } from "react";
import {
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Alert,
  Divider,
  Tabs,
  Tab,
  Autocomplete,
  CircularProgress,
} from "@mui/material";
import { getRequestWithToken } from "../../utils/ApiRequests";

const SUPPORTED_LANGUAGES = [
  { code: "en", name: "English" },
  { code: "hi", name: "हिंदी (Hindi)" },
  { code: "hn", name: "Hinglish" },
  { code: "ta", name: "தமிழ் (Tamil)" },
  { code: "te", name: "తెలుగు (Telugu)" },
  { code: "kn", name: "ಕನ್ನಡ (Kannada)" },
  { code: "bn", name: "বাংলা (Bengali)" },
  { code: "gu", name: "ગુજરાતી (Gujarati)" },
  { code: "mr", name: "मराठी (Marathi)" },
];

const CANDIDATE_ROLE_MAPPING_API_BASE = "../v3/admin/candidate-role-mappings";

export const createInitialRoleData = (role = {}) => ({
  name: role.name || "",
  slug: role.slug || "",
  metaTitle: role.metaTitle || "",
  metaDescription: role.metaDescription || "",
  ogTitle: role.ogTitle || "",
  ogDescription: role.ogDescription || "",
  ogImage: role.ogImage || "",
  twitterTitle: role.twitterTitle || "",
  twitterDescription: role.twitterDescription || "",
  heading: role.heading || "",
  heading_hi: role.heading_hi || "",
  heading_hn: role.heading_hn || "",
  heading_ta: role.heading_ta || "",
  heading_te: role.heading_te || "",
  heading_kn: role.heading_kn || "",
  heading_bn: role.heading_bn || "",
  heading_gu: role.heading_gu || "",
  heading_mr: role.heading_mr || "",
  subheading: role.subheading || "",
  subheading_hi: role.subheading_hi || "",
  subheading_hn: role.subheading_hn || "",
  subheading_ta: role.subheading_ta || "",
  subheading_te: role.subheading_te || "",
  subheading_kn: role.subheading_kn || "",
  subheading_bn: role.subheading_bn || "",
  subheading_gu: role.subheading_gu || "",
  subheading_mr: role.subheading_mr || "",
  paragraph: role.paragraph || "",
  paragraph_hi: role.paragraph_hi || "",
  paragraph_hn: role.paragraph_hn || "",
  paragraph_ta: role.paragraph_ta || "",
  paragraph_te: role.paragraph_te || "",
  paragraph_kn: role.paragraph_kn || "",
  paragraph_bn: role.paragraph_bn || "",
  paragraph_gu: role.paragraph_gu || "",
  paragraph_mr: role.paragraph_mr || "",
});

export const getRoleManagerValidationError = (roleData, selectedRoleMapping) => {
  if (!roleData.name.trim()) return "Role name is required";
  if (!roleData.slug.trim()) return "Slug is required";
  if (!roleData.metaTitle.trim()) return "Meta title is required";
  if (!roleData.metaDescription.trim()) return "Meta description is required";
  if (!selectedRoleMapping?._id) return "Candidate role mapping is required";
  return "";
};

export const buildRoleManagerSubmitData = (roleData, selectedRoleMapping) => ({
  ...roleData,
  candidateRoleMappingRef: selectedRoleMapping?._id || "",
});

export const getCandidateRoleSnapshot = (role = {}) => {
  const snapshot = role.candidateRole || {};
  return {
    familyId: snapshot.familyId || snapshot.family_id || snapshot.family?.family_id || role.familyId || "",
    groupId: snapshot.groupId || snapshot.roleGroupId || snapshot.role_group_id || snapshot.role_group?.role_group_id || role.groupId || "",
    roleId: snapshot.roleId || snapshot.role_id || snapshot.role?.role_id || role.roleId || "",
    mappingRef: role.candidateRoleMappingRef?._id || role.candidateRoleMappingRef || snapshot.mappingId || snapshot.mappingRef || "",
  };
};

export const getFamilyLabel = (family) => (
  family?.family_ui_label || family?.family_name || family?.name?.en || family?.family_id || ""
);

export const getGroupLabel = (group) => (
  group?.role_group_ui_label || group?.role_group_name || group?.name?.en || group?.role_group_id || ""
);

export const getRoleMappingLabel = (mapping) => (
  mapping?.display?.display_title
  || mapping?.display?.candidate_display_title
  || mapping?.role?.role_ui_label
  || mapping?.role?.ui_label
  || mapping?.role?.role_name
  || mapping?.role?.name?.en
  || mapping?.role?.role_id
  || ""
);

const RoleManager = ({ open, onClose, onCreateRole, editingRole = null, onUpdateRole }) => {
  const [roleData, setRoleData] = useState(createInitialRoleData());
  const [error, setError] = useState("");
  const [currentLanguageTab, setCurrentLanguageTab] = useState("en");
  const [families, setFamilies] = useState([]);
  const [groups, setGroups] = useState([]);
  const [roleMappings, setRoleMappings] = useState([]);
  const [selectedFamily, setSelectedFamily] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedRoleMapping, setSelectedRoleMapping] = useState(null);
  const [loadingFamilies, setLoadingFamilies] = useState(false);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [mappingLoadError, setMappingLoadError] = useState("");

  const loadFamilies = async () => {
    setLoadingFamilies(true);
    setMappingLoadError("");
    try {
      const res = await getRequestWithToken(`${CANDIDATE_ROLE_MAPPING_API_BASE}/families/list`);
      const nextFamilies = Array.isArray(res.data?.data) ? res.data.data : [];
      const sorted = nextFamilies.sort((a, b) => getFamilyLabel(a).localeCompare(getFamilyLabel(b), "en", { sensitivity: "base" }));
      setFamilies(sorted);
      return sorted;
    } catch (err) {
      setMappingLoadError(err?.response?.data?.msg || err?.message || "Failed to load families");
      setFamilies([]);
      return [];
    } finally {
      setLoadingFamilies(false);
    }
  };

  const loadGroups = async (family) => {
    if (!family?._id) {
      setGroups([]);
      return [];
    }
    setLoadingGroups(true);
    setMappingLoadError("");
    try {
      const res = await getRequestWithToken(`${CANDIDATE_ROLE_MAPPING_API_BASE}/families/${family._id}/groups`);
      const nextGroups = Array.isArray(res.data?.data) ? res.data.data : [];
      const sorted = nextGroups.sort((a, b) => getGroupLabel(a).localeCompare(getGroupLabel(b), "en", { sensitivity: "base" }));
      setGroups(sorted);
      return sorted;
    } catch (err) {
      setMappingLoadError(err?.response?.data?.msg || err?.message || "Failed to load groups");
      setGroups([]);
      return [];
    } finally {
      setLoadingGroups(false);
    }
  };

  const loadRoleMappings = async (group) => {
    if (!group?._id) {
      setRoleMappings([]);
      return [];
    }
    setLoadingRoles(true);
    setMappingLoadError("");
    try {
      const res = await getRequestWithToken(`${CANDIDATE_ROLE_MAPPING_API_BASE}/groups/${group._id}/roles`);
      const nextRoles = Array.isArray(res.data?.data) ? res.data.data : [];
      const sorted = nextRoles.sort((a, b) => getRoleMappingLabel(a).localeCompare(getRoleMappingLabel(b), "en", { sensitivity: "base" }));
      setRoleMappings(sorted);
      return sorted;
    } catch (err) {
      setMappingLoadError(err?.response?.data?.msg || err?.message || "Failed to load roles");
      setRoleMappings([]);
      return [];
    } finally {
      setLoadingRoles(false);
    }
  };

  React.useEffect(() => {
    if (!open) return;

    let mounted = true;
    const hydrate = async () => {
      setRoleData(createInitialRoleData(editingRole || {}));
      setSelectedFamily(null);
      setSelectedGroup(null);
      setSelectedRoleMapping(null);
      setGroups([]);
      setRoleMappings([]);

      const snapshot = getCandidateRoleSnapshot(editingRole || {});
      const nextFamilies = await loadFamilies();
      if (!mounted || !snapshot.familyId) return;

      const family = nextFamilies.find((item) => item.family_id === snapshot.familyId) || null;
      setSelectedFamily(family);
      const nextGroups = family ? await loadGroups(family) : [];
      if (!mounted || !snapshot.groupId) return;

      const group = nextGroups.find((item) => item.role_group_id === snapshot.groupId) || null;
      setSelectedGroup(group);
      const nextRoles = group ? await loadRoleMappings(group) : [];
      if (!mounted) return;

      const roleMapping = nextRoles.find((item) => (
        item._id === snapshot.mappingRef || item.role?.role_id === snapshot.roleId
      )) || null;
      setSelectedRoleMapping(roleMapping);
    };

    hydrate();
    return () => { mounted = false; };
  }, [open, editingRole]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setRoleData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Auto-generate slug from name
    if (name === "name") {
      setRoleData((prev) => ({
        ...prev,
        slug: value
          .toLowerCase()
          .replace(/\s+/g, "-")
          .replace(/[^\w-]/g, ""),
      }));
    }
  };

  const handleFamilyChange = async (family) => {
    setSelectedFamily(family);
    setSelectedGroup(null);
    setSelectedRoleMapping(null);
    setGroups([]);
    setRoleMappings([]);
    if (family) await loadGroups(family);
  };

  const handleGroupChange = async (group) => {
    setSelectedGroup(group);
    setSelectedRoleMapping(null);
    setRoleMappings([]);
    if (group) await loadRoleMappings(group);
  };

  const validateForm = () => {
    const validationError = getRoleManagerValidationError(roleData, selectedRoleMapping);
    if (validationError) {
      setError(validationError);
      return false;
    }
    return true;
  };

  const handleSubmit = () => {
    setError("");
    if (!validateForm()) {
      return;
    }
    const submitData = buildRoleManagerSubmitData(roleData, selectedRoleMapping);
    if (editingRole) {
      onUpdateRole(editingRole._id, submitData);
    } else {
      onCreateRole(submitData);
    }
    // Do not reset fields on submit
  };

  const handleClose = () => {
    setError("");
    setMappingLoadError("");
    setRoleData(createInitialRoleData());
    setFamilies([]);
    setGroups([]);
    setRoleMappings([]);
    setSelectedFamily(null);
    setSelectedGroup(null);
    setSelectedRoleMapping(null);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        // Only close if Cancel button is clicked (reason will be undefined)
        // Block closing from backdrop (reason = "backdropClick")
        if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
          handleClose();
        }
      }}
      disableEscapeKeyDown
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>{editingRole ? "Edit Role" : "Create New Role"}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>
          {error && <Alert severity="error">{error}</Alert>}
          {mappingLoadError && <Alert severity="error">{mappingLoadError}</Alert>}

          {/* Basic Info */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Basic Information
            </Typography>
            <TextField
              fullWidth
              label="Role Name"
              name="name"
              value={roleData.name}
              onChange={handleInputChange}
              placeholder="e.g., Rider, Driver"
              size="small"
              sx={{ mb: 1 }}
            />
            <TextField
              fullWidth
              label="Slug URL"
              name="slug"
              value={roleData.slug}
              onChange={handleInputChange}
              placeholder="e.g., rider, driver"
              size="small"
              helperText="Auto-generated from role name"
            />
          </Box>

          <Divider />

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Candidate Role Mapping
            </Typography>
            <Autocomplete
              size="small"
              options={families}
              value={selectedFamily}
              onChange={(event, value) => handleFamilyChange(value)}
              getOptionLabel={getFamilyLabel}
              isOptionEqualToValue={(option, value) => option.family_id === value.family_id}
              loading={loadingFamilies}
              noOptionsText={loadingFamilies ? "Loading families..." : "No families found"}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Family *"
                  placeholder="Search family"
                  sx={{ mb: 1 }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingFamilies ? <CircularProgress color="inherit" size={18} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
            <Autocomplete
              size="small"
              options={groups}
              value={selectedGroup}
              onChange={(event, value) => handleGroupChange(value)}
              getOptionLabel={getGroupLabel}
              isOptionEqualToValue={(option, value) => option.role_group_id === value.role_group_id}
              loading={loadingGroups}
              disabled={!selectedFamily}
              noOptionsText={loadingGroups ? "Loading groups..." : "No groups found"}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Group *"
                  placeholder={selectedFamily ? "Search group" : "Select a family first"}
                  sx={{ mb: 1 }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingGroups ? <CircularProgress color="inherit" size={18} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
            <Autocomplete
              size="small"
              options={roleMappings}
              value={selectedRoleMapping}
              onChange={(event, value) => setSelectedRoleMapping(value)}
              getOptionLabel={getRoleMappingLabel}
              isOptionEqualToValue={(option, value) => option._id === value._id}
              loading={loadingRoles}
              disabled={!selectedGroup}
              noOptionsText={loadingRoles ? "Loading roles..." : "No roles found"}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Role *"
                  placeholder={selectedGroup ? "Search role" : "Select a group first"}
                  helperText={selectedRoleMapping ? `Mapping ID: ${selectedRoleMapping._id}` : ""}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingRoles ? <CircularProgress color="inherit" size={18} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Box>

          <Divider />

          {/* SEO Meta Tags */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              SEO Metadata
            </Typography>
            <TextField
              fullWidth
              label="Meta Title"
              name="metaTitle"
              value={roleData.metaTitle}
              onChange={handleInputChange}
              placeholder="Page title for search engines"
              size="small"
              sx={{ mb: 1 }}
              helperText="Recommended: 50-60 characters"
            />
            <TextField
              fullWidth
              label="Meta Description"
              name="metaDescription"
              value={roleData.metaDescription}
              onChange={handleInputChange}
              placeholder="Page description for search engines"
              size="small"
              multiline
              rows={2}
              sx={{ mb: 1 }}
              helperText="Recommended: 150-160 characters"
            />
          </Box>

          <Divider />

          {/* Multilingual Content */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Dynamic Content (Multilingual)
            </Typography>

            <Tabs
              value={currentLanguageTab}
              onChange={(e, newValue) => setCurrentLanguageTab(newValue)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}
            >
              {SUPPORTED_LANGUAGES.map((lang) => (
                <Tab key={lang.code} label={lang.name} value={lang.code} />
              ))}
            </Tabs>

            <TextField
              fullWidth
              label={`Heading (${SUPPORTED_LANGUAGES.find(l => l.code === currentLanguageTab)?.name})`}
              name={currentLanguageTab === "en" ? "heading" : `heading_${currentLanguageTab}`}
              value={currentLanguageTab === "en" ? roleData.heading : roleData[`heading_${currentLanguageTab}`]}
              onChange={handleInputChange}
              // placeholder="Main heading for the role"
              size="small"
              sx={{ mb: 1 }}
            />

            <TextField
              fullWidth
              label={`Subheading (${SUPPORTED_LANGUAGES.find(l => l.code === currentLanguageTab)?.name})`}
              name={currentLanguageTab === "en" ? "subheading" : `subheading_${currentLanguageTab}`}
              value={currentLanguageTab === "en" ? roleData.subheading : roleData[`subheading_${currentLanguageTab}`]}
              onChange={handleInputChange}
              // placeholder="Subheading for the role"
              size="small"
              sx={{ mb: 1 }}
            />

            <TextField
              fullWidth
              label={`Paragraph (${SUPPORTED_LANGUAGES.find(l => l.code === currentLanguageTab)?.name})`}
              name={currentLanguageTab === "en" ? "paragraph" : `paragraph_${currentLanguageTab}`}
              value={currentLanguageTab === "en" ? roleData.paragraph : roleData[`paragraph_${currentLanguageTab}`]}
              onChange={handleInputChange}
              // placeholder="Detailed description paragraph"
              size="small"
              multiline
              rows={3}
            />
          </Box>

          <Divider />

          {/* OpenGraph Tags */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Open Graph (Social Media)
            </Typography>
            <TextField
              fullWidth
              label="OG Title"
              name="ogTitle"
              value={roleData.ogTitle}
              onChange={handleInputChange}
              placeholder="Title when shared on social media"
              size="small"
              sx={{ mb: 1 }}
            />
            <TextField
              fullWidth
              label="OG Description"
              name="ogDescription"
              value={roleData.ogDescription}
              onChange={handleInputChange}
              placeholder="Description when shared on social media"
              size="small"
              multiline
              rows={2}
              sx={{ mb: 1 }}
            />
            <TextField
              fullWidth
              label="OG Image URL"
              name="ogImage"
              value={roleData.ogImage}
              onChange={handleInputChange}
              placeholder="https://example.com/image.jpg"
              size="small"
            />
          </Box>

          <Divider />

          <Alert severity="info">
            <Typography variant="caption">
              <strong>Supported Languages:</strong> English, हिंदी, தமிழ், తెలుగు, ಕನ್ನಡ, বাংলা, ગુજરાતી, मराठी
            </Typography>
          </Alert>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" color="primary">
          {editingRole ? "Update Role" : "Create Role"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RoleManager;
